import { formatNumber as angularFormatNumber, formatCurrency } from '@angular/common';
import { CustomPercentagePipe } from '@pipes/custom-percentage.pipe';
import { FormatNumberWithSuffixPipe } from 'ngx-lib';

export function isEmptyValue(value: unknown): value is null | undefined | '' {
    return value === null || value === undefined || value === '';
}

export function normalizeNumberInput(value: number | string | null | undefined): number | null {
    if (isEmptyValue(value)) {
        return null;
    }

    const numericValue = typeof value === 'number' ? value : Number.parseFloat(value.toString().replaceAll(',', ''));

    return Number.isNaN(numericValue) ? null : numericValue;
}

export function currencyFormatter(
    value: number,
    locale = 'en-US',
    symbol = '$',
    currencyCode = 'USD',
    digitsInfo = '1.2-2'
): string {
    if (value === undefined || value === null || Number.isNaN(Number(value))) return '';

    return formatCurrency(value, locale, symbol, currencyCode, digitsInfo);
}

export function currencyFormatterFromInput(
    value: number | string | null | undefined,
    locale = 'en-US',
    symbol = '$',
    currencyCode = 'USD',
    digitsInfo = '1.2-2'
): string {
    const numeric = normalizeNumberInput(value);
    if (numeric === null) return '';
    return currencyFormatter(numeric, locale, symbol, currencyCode, digitsInfo);
}

export function decimalFormatter(
    value: number | string | null | undefined,
    locale = 'en-US',
    digitsInfo = '1.0-2'
): string {
    const numeric = normalizeNumberInput(value);
    if (numeric === null) return '';
    return angularFormatNumber(numeric, locale, digitsInfo);
}

export function formatArrayValue(value: string[] | string | null | undefined): string {
    if (isEmptyValue(value)) return '';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '';
    return String(value);
}

export function formatNumber(number: number, decimalPlaces?: number) {
    if (decimalPlaces != null) return Number(number).toFixed(decimalPlaces);
    return Math.floor(number).toLocaleString();
}

export function percentageFormatter(value: number | null) {
    if (value === undefined || value === null || Number.isNaN(value)) return null;
    const displayValue = value * 100;
    return Number(displayValue).toFixed(2) + '%';
}

export function truncateValue(value: string, maxLength = 20): string {
    return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
}

export function formatCurrencyCompact(val: string): string {
    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        style: 'currency',
        currency: 'USD'
    }).format(Number.parseInt(val));
}

export function formatNumbersCompact(val: string): string {
    return new Intl.NumberFormat('en-US', {
        notation: 'compact'
    }).format(Number.parseInt(val));
}

// This function is used to format numbers with suffixes like 'k' for thousands and 'M' for millions, using the logic defined in the FormatNumberWithSuffixPipe.
export function formatNumberWithSuffix(val: number): string {
    return '$' + new FormatNumberWithSuffixPipe().transform(val);
}

// This function is used to format values as percentages, using the logic defined in the CustomPercentagePipe.
export function formatCustomPercentage(value?: number | string | null): string | null {
    return new CustomPercentagePipe().transform(value);
}

export function formatBooleanYesNo(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return '';
}
